import { useState } from "react";
import { Sparkles, Loader2, X } from "lucide-react";
import { api, formatApiError } from "../api/client";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { toast } from "sonner";

const QUICK = [
  "Reescribir más formal",
  "Reescribir más conciso",
  "Expandir esta sección",
  "Añadir ejemplos",
];

export default function AIAssistantPanel({ open, onClose, docId, selectedText, onInsert, onReplace }) {
  const [instruction, setInstruction] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState("");

  const run = async (instr) => {
    const i = instr || instruction;
    if (!i.trim()) return toast.error("Indica una instrucción.");
    setBusy(true); setResult("");
    try {
      const { data } = await api.post("/ai/regenerate-section", {
        document_id: docId,
        section_instruction: i,
        selected_text: selectedText || null,
      });
      setResult(data.new_text || "");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  if (!open) return null;

  return (
    <div className="border border-slate-200 rounded-md bg-white p-4" data-testid="ai-panel">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-primary font-semibold text-[14px]"><Sparkles className="h-4 w-4" /> Asistente IA</div>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="h-4 w-4" /></button>
      </div>

      {selectedText && (
        <div className="mt-3">
          <div className="text-[11px] uppercase tracking-wide text-slate-400 mb-1">Texto seleccionado</div>
          <div className="text-[12px] text-slate-700 bg-slate-50 border border-slate-200 rounded p-2 max-h-32 overflow-y-auto whitespace-pre-wrap">
            {selectedText}
          </div>
        </div>
      )}

      <div className="mt-3">
        <Label>Instrucción</Label>
        <Input value={instruction} onChange={(e) => setInstruction(e.target.value)} placeholder="Ej. Reformula con un tono más formal" data-testid="ai-instruction" />
      </div>

      <div className="mt-2 flex flex-wrap gap-2">
        {QUICK.map((q) => (
          <button key={q} onClick={() => { setInstruction(q); run(q); }} disabled={busy}
                  className="text-[12px] border border-slate-200 rounded-full px-3 py-1 hover:bg-slate-50 disabled:opacity-50"
                  data-testid={`ai-quick-${q.replace(/ /g, "-").toLowerCase()}`}>
            {q}
          </button>
        ))}
      </div>

      <Button className="w-full mt-3" disabled={busy} onClick={() => run()} data-testid="ai-run">
        {busy ? (<><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Generando...</>) : "Generar"}
      </Button>

      {result && (
        <div className="mt-4">
          <div className="text-[11px] uppercase tracking-wide text-slate-400 mb-1">Resultado</div>
          <div className="text-[13px] text-slate-800 bg-slate-50 border border-slate-200 rounded p-2 max-h-48 overflow-y-auto whitespace-pre-wrap">
            {result}
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            {selectedText && (
              <Button size="sm" onClick={() => { onReplace(result); setResult(""); }} data-testid="ai-replace">Reemplazar selección</Button>
            )}
            <Button size="sm" variant="outline" onClick={() => { onInsert(result); setResult(""); }} data-testid="ai-insert">Insertar tras la selección</Button>
            <Button size="sm" variant="ghost" onClick={() => setResult("")} data-testid="ai-discard">Descartar</Button>
          </div>
        </div>
      )}
    </div>
  );
}
